/**
 * Product photos: their sizes, their keys, and which one a colourway shows
 * (SCOPE §5.4, decision 36).
 */

/** Every photo is stored in these widths, as WebP. Never enlarged past the original. */
export const IMAGE_RENDITIONS = {
  /** Register search rows. */
  thumb: 96,
  /** A cart line; the product's page in the POS. */
  register: 320,
  /** The storefront grid. */
  web: 800,
  /** The storefront's product page. */
  zoom: 1600,
} as const;

export type ImageRendition = keyof typeof IMAGE_RENDITIONS;

export const IMAGE_RENDITION_NAMES = Object.keys(IMAGE_RENDITIONS) as ImageRendition[];

/** The largest file a buyer may upload: a phone photo, with room to spare. */
export const IMAGE_MAX_BYTES = 15 * 1024 * 1024;

/**
 * The folder one photo's files live in.
 *
 * `tenant/{tenantId}/products/{productId}/{contentHash}` — the tenant prefix is
 * §12.3's rule, and the hash is what makes every URL immutable: a changed photo
 * is a new folder, never a changed file.
 */
export function imageFolder(input: {
  tenantId: string;
  productId: string;
  contentHash: string;
}): string {
  return `tenant/${input.tenantId}/products/${input.productId}/${input.contentHash}`;
}

export function renditionKey(folder: string, rendition: ImageRendition): string {
  return `${folder}/${rendition}.webp`;
}

/** The folder a stored key belongs to — everything up to the last slash. */
export function folderOfKey(key: string): string {
  return key.slice(0, key.lastIndexOf('/'));
}

export interface PlacedImage {
  readonly colorName: string | null;
  readonly sortOrder: number;
}

/**
 * Which photo a colourway shows.
 *
 * Colour-exact first, then a photo of the whole style, then whatever comes
 * first — so a style with a single photo looks complete everywhere the moment
 * it lands. Colour is matched by name, ignoring case and spaces at the ends,
 * because colour is free text on the variant.
 */
export function imageForColor<T extends PlacedImage>(
  images: readonly T[],
  colorName: string | null,
): T | null {
  if (images.length === 0) return null;
  const ordered = [...images].sort((a, b) => a.sortOrder - b.sortOrder);
  const wanted = colorName?.trim().toLowerCase() ?? null;
  if (wanted !== null && wanted !== '') {
    const exact = ordered.find((image) => image.colorName?.trim().toLowerCase() === wanted);
    if (exact !== undefined) return exact;
  }
  return ordered.find((image) => image.colorName === null) ?? ordered[0]!;
}
